import React, { useState, useEffect, useRef } from "react";

// ✅ `initialSNSData` をエクスポート
export const initialSNSData = {
    "X": "",
    "Instagram": "",
    "Facebook": "",
    "LinkedIn": ""
};

const SNSParserComponent = ({ loadedContent, onStrategyUpdate }) => {
    const [parsedData, setParsedData] = useState(() => ({
        snsStrategy: { ...initialSNSData }
    }));

    const [selectedSNS, setSelectedSNS] = useState([]); // ✅ 選択されたSNSを管理

    const prevParsedDataRef = useRef(null);

    useEffect(() => {
        if (!loadedContent || typeof loadedContent !== "string" || loadedContent.trim() === "") {
            console.warn("⚠️ `loadedContent` が無効なので処理をスキップ");
            return;
        }

        const parsedStrategy = parseSNSStrategy(loadedContent);

        if (!parsedStrategy || Object.keys(parsedStrategy).length === 0) {
            console.warn("⚠️ `parsedStrategy` が無効なため `snsStrategy` を更新しません");
            return;
        }

        setParsedData(prev => {
            const updatedData = {
                ...prev,
                snsStrategy: { ...initialSNSData, ...parsedStrategy }
            };

            // 🔥 **前回と同じなら更新しない**
            if (JSON.stringify(prevParsedDataRef.current) === JSON.stringify(updatedData.snsStrategy)) {
                //console.log("⚠️ `setParsedData` は更新不要 (同じデータ)");
                return prev;
            }

            prevParsedDataRef.current = updatedData.snsStrategy;  // **新しいデータを保存**
            //console.log("📌 [DEBUG] `setParsedData` 更新:", updatedData);
            return updatedData;
        });

        setTimeout(() => {
            onStrategyUpdate({ snsStrategy: parsedStrategy });
            //console.log("📌 [DEBUG] `onStrategyUpdate` 送信:", parsedStrategy);
        }, 0);

    }, [loadedContent]);


    useEffect(() => {
        onStrategyUpdate(parsedData.snsStrategy);
    }, [parsedData.snsStrategy, onStrategyUpdate]);    
    
    return (
        <div>
            <h4>📲 SNSと運用戦略</h4>
            <div className="sns-strategy-container">
                {Object.keys(parsedData.snsStrategy).map((sns) => {
                    const hasContent = parsedData.snsStrategy[sns]?.trim() !== "";

                    return (
                        <div key={sns} className="sns-strategy-item">
                            <label>
                                <input
                                    type="checkbox"
                                    value={sns} // ✅ SNS名を value に設定（直接取得する用）
                                    defaultChecked={hasContent || selectedSNS.includes(sns)} // ✅ `textarea` が空でなければチェックON
                                />
                                {sns}
                            </label>
                            <textarea
                                name={sns} // ✅ `querySelector` で取得するために `name` をセット
                                className="sns-strategy-document"
                                defaultValue={parsedData.snsStrategy[sns] || ""}
                                placeholder={`${sns} の運用戦略を入力`}
                                onInput={(e) => {
                                    const checkbox = document.querySelector(`input[value="${sns}"]`);
                                    if (checkbox) {
                                        checkbox.checked = e.target.value.trim() !== "";
                                    }
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ✅ **SNS戦略を解析する関数**
export const parseSNSStrategy = (text) => {
    const strategy = {};
    const lines = text.split("\n").map(line => line.trim());
    let currentPlatform = "";
    let currentContent = [];

    const snsPlatforms = {
        "Instagram": ["Instagram", "インスタ", "インスタグラム"],
        "X": ["X", "X旧Twitter", "Twitter", "ツイッター"],
        "Facebook": ["Facebook", "フェイスブック"],
        "LinkedIn": ["LinkedIn", "リンクトイン"]
    };

    lines.forEach((line) => {
        const cleanedLine = line
            .replace(/\s*\(.*?\)\s*/g, " ")  // `(旧Twitter)` のようなカッコ内テキストを削除
            .replace(/\*/g, "")              // `*` を削除
            .replace(/:$/, "")               // `:` を削除
            .trim();

        const matchedPlatform = Object.entries(snsPlatforms).find(([platform, aliases]) => {
            return aliases.some(alias => {
                const regex = new RegExp(`^-\\s*${alias}\\s*$`, "i");
                return regex.test(cleanedLine);
            });
        });

        if (matchedPlatform) {
            const [platformKey] = matchedPlatform;
            if (line.includes("(オミット)")) {
                return;
            }
            if (currentPlatform) {
                strategy[currentPlatform] = currentContent.join("\n").trim();
            }
            currentPlatform = platformKey;
            currentContent = [];
        } else if (currentPlatform) {
            currentContent.push(line);
        }
    });

    if (currentPlatform) {
        strategy[currentPlatform] = currentContent.join("\n").trim();
    }

    //console.log("📌 [DEBUG] parseSNSStrategy の戻り値:", JSON.stringify(strategy, null, 2));

    // **型チェックしてオブジェクト以外を防ぐ**
    if (typeof strategy !== "object" || Array.isArray(strategy)) {
        console.error("🚨 `parseSNSStrategy` の戻り値がオブジェクトではない！", strategy);
        return {};
    }

    return strategy;
};

export default SNSParserComponent;
